/**
 * Provider della scelta del tema (F-070).
 *
 * Non scrive `data-theme` al primo render: l'ha già scritto lo script in
 * `index.html`, prima del primo dipinto. Farlo qui mostrerebbe il tema
 * sbagliato per un fotogramma — un lampo bianco per chi tiene lo schermo scuro.
 * Da qui in poi lo tiene aggiornato, e riascolta il sistema per chi resta su
 * `auto`.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyTheme,
  DARK_QUERY,
  readStoredChoice,
  storeChoice,
  systemTheme,
  ThemeContext,
  type ResolvedTheme,
  type ThemeChoice,
  type ThemeContextValue,
} from './theme-context';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStoredChoice);
  const [systemPreference, setSystemPreference] = useState<ResolvedTheme>(systemTheme);

  // Il sistema può cambiare tema mentre l'applicazione è aperta: con `auto`
  // selezionato l'interfaccia deve seguirlo senza un ricaricamento.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setSystemPreference(event.matches ? 'dark' : 'light');
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme = choice === 'auto' ? systemPreference : choice;

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    storeChoice(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolved, setChoice }),
    [choice, resolved, setChoice],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
